/* eslint-disable no-nested-ternary */
/* eslint-disable no-return-assign */
/* eslint-disable react/no-array-index-key */
import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import ResizeObserver from 'resize-observer-polyfill';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import UnifiedDayHeader from './UnifiedDayHeader';
import DraggableContainer from './DraggableContainer';
import Text from '../../../../shared/components/Text/Text';
import abbreviateStatNumber from '../../../../shared/utils/abbreviateStatNumber';
import useAbbreviateOnOverflow from '../../hooks/useAbbreviateOnOverflow';
import AnimatedNumber from './AnimatedNumber';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setExpandedItems,
  setWeekViewExpandedDay,
  setCurrentlyDisplayedWeekdays,
  setCurrentlyDisplayedProgramDays,
  setSelectedProgramDayNumber,
  setPreviewDayNumber,
} from '../../ducks/calendarActions';
import {
  ICON_RAIL_WIDTH,
  SIDEBAR_TABLET_BREAKPOINT,
  SIDEBAR_DRAWER_BREAKPOINT,
  getSideBarOffset,
} from '../../constants/sidebarDimensions';
import getProgramDayTotals from '../../utils/programDayTotals';

const WeekContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 100%;
  gap: ${(props) => (props.viewType === 'weekView' ? '8px' : '1px')};
  background-color: ${({ theme }) => theme.colors.backgroundApp};
  ${(props) => props.isTvMode && 'padding: 8px 8px 0 8px;'}
`;

const ScrollIconBox = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  
   &:hover {
    border-color: ${({ theme }) => theme.colors.textTertiary};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    transform: scale(0.96);
  }

  left: ${(props) => {
    if (!props.leftAnchor) return 'auto';
    if (props.tvMode) return '12px';
    return props.sidebarOpen ? '444px' : '60px';
  }};
  ${(props) => (props.leftAnchor && !props.tvMode
    ? `@media screen and (max-width: 1023px) { left: ${props.sidebarOpen ? '360px' : '60px'}; }`
    : '')}

  @media screen and (max-width: 767px) {
    ${(props) => (props.sidebarOpen ? 'display: none;' : '')}
  }
`;

const MarginRight = styled('div')`
  position: fixed;
  top: ${(props) => props.headerHeight + (props.programType === 'free-form-program' ? 0 : 32) + 49}px;
  bottom: 48px;
  right: 0;
  width: 8px;
  z-index: 90;
  background-color: ${({ theme }) => theme.colors.backgroundApp};

  @media screen and (max-width: 767px) {
    ${(props) => (props.isPrimaryAnimatedSideBarOpen ? 'display: none;' : '')}
  }
`;

const MarginLeft = styled('div')`
  position: fixed;
  top: ${(props) => props.headerHeight + (props.programType === 'free-form-program' ? 0 : 32) + 49}px;
  bottom: 48px;
  left: ${(props) => `${getSideBarOffset(props.isPrimaryAnimatedSideBarOpen)}px`};
  width: 8px;
  z-index: 90;
  background-color: ${({ theme }) => theme.colors.backgroundApp};

  @media screen and (max-width: ${SIDEBAR_TABLET_BREAKPOINT}px) {
    left: ${(props) => `${getSideBarOffset(props.isPrimaryAnimatedSideBarOpen, true)}px`};
  }

  /* Drawer breakpoint: the rail is hidden and the sidebar overlays rather than
     reserving space, so there is nothing to offset past. Without this the strip
     sits 48px in and paints over the first day column. */
  @media screen and (max-width: ${SIDEBAR_DRAWER_BREAKPOINT}px) {
    left: 0;
  }
`;

const DayWrapper = styled('div')`
  flex: ${(props) => (props.isExpanded && props.weekDatesOrDaysLength > 1 ? '0 0 800px' : '1')};
  min-width: 208px;
  display: flex;
  flex-direction: column;

  /* Phones: an expanded day is pinned to 800px on desktop, which overflows a
     phone and cuts content off. Make it exactly the width of the visible
     content panel (viewport minus the 48px icon rail) so it matches day view;
     sibling days keep their 175px min-width, so the week still scrolls
     horizontally across columns. Desktop unchanged. */
  @media (max-width: 480px) {
    flex: ${(props) => (props.isExpanded && props.weekDatesOrDaysLength > 1 ? `0 0 calc(100vw - ${ICON_RAIL_WIDTH}px)` : '1')};
    min-width: ${(props) => (props.isExpanded && props.weekDatesOrDaysLength > 1 ? `calc(100vw - ${ICON_RAIL_WIDTH}px)` : '175px')};
  }
`;

const DayColumn = styled('div')`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 0 0 4px 4px;
  flex: 1;
  padding-top: ${(props) => !props.isWeekDayItemsLoading && '8px'};
  padding-left: ${(props) => props.addPaddingLeftAndRight && '5px'};
  padding-right: ${(props) => props.addPaddingLeftAndRight && '5px'};
`;

const StatsBar = styled('div')`
  position: relative;
  background: ${({ theme }) => theme.colors.surface};
  min-height: 40px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;

  /* Phones: hide the Sets/Reps/Volume bar to reclaim vertical space. */
  @media (max-width: 480px) {
    display: none;
  }
`;

const STAT_ROW_STYLE = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  gap: '16px',
};

/** Per-day bottom stats bar (S/R/V). Shows full numbers, abbreviating a value only when the full
 * row would overflow the column — measured off a hidden static full-value copy (so the count-up
 * animation doesn't skew the decision). */
const WeekDayStatsBar = ({
  isExpanded, sets, reps, volume,
}) => {
  const { colors } = useTheme();
  const { containerRef, measureRef, abbreviate } = useAbbreviateOnOverflow(
    `${sets}-${reps}-${volume}-${isExpanded}`,
  );
  const labels = isExpanded
    ? { s: 'Sets', r: 'Reps', v: 'Volume' }
    : { s: 'S', r: 'R', v: 'V' };
  const item = (label, node) => (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <Text fontSize='12px' color={colors.textSecondary}>{label}</Text>
      <Text
        marginLeft='5px'
        marginTop='2px'
        fontSize='12px'
        color={colors.text}
        fontWeight='700'
      >
        {node}
      </Text>
    </div>
  );
  const fmt = abbreviate ? abbreviateStatNumber : undefined;
  return (
    <StatsBar>
      <div ref={containerRef} style={STAT_ROW_STYLE}>
        {item(labels.s, <AnimatedNumber value={sets} format={fmt} />)}
        {item(labels.r, <AnimatedNumber value={reps} format={fmt} />)}
        {item(labels.v, <AnimatedNumber value={volume} format={fmt} />)}
      </div>
      <div
        ref={measureRef}
        aria-hidden
        style={{
          ...STAT_ROW_STYLE,
          width: 'auto',
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        {item(labels.s, sets.toLocaleString('en-US'))}
        {item(labels.r, reps.toLocaleString('en-US'))}
        {item(labels.v, volume.toLocaleString('en-US'))}
      </div>
    </StatsBar>
  );
};

WeekDayStatsBar.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  sets: PropTypes.number.isRequired,
  reps: PropTypes.number.isRequired,
  volume: PropTypes.number.isRequired,
};

const WeekView = ({ isWeekDayItemsLoading }) => {
  const dispatch = useDispatch();
  const weekViewContainerRef = useRef();
  const horizontalItemRefs = useRef([]);
  // Tracks whether the groupByFilter effect has run once, so the preview re-sync fires only on an
  // actual Group By toggle (not on mount / view entry).
  const didGroupByMount = useRef(false);
  const shimmerCount = [1, 2, 3, 4, 5];
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const groupByFilter = useSelector(
    (state) => state.calendar.data.groupByFilter,
  );

  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector((state) => state.calendar.data.afterRecurringWorkouts);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const expandedItems = useSelector((state) => state.calendar.data.expandedItems);
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const programItems = useSelector((state) => state.calendar.data.nonDateBasedProgramItems);
  const expandedDay = useSelector((state) => state.calendar.data.weekViewExpandedDay);
  const selectedNumberOfDaysToShowInWeek = useSelector(
    (state) => state.calendar.data.selectedNumberOfDaysToShowInWeek,
  );
  const selectedDaysOfWeekToShowInCurrentWeek = useSelector(
    (state) => state.calendar.data.selectedDaysOfWeekToShowInCurrentWeek,
  );
  const animatedSidebarContainerWidth = useSelector(
    (state) => state.calendar.data.animatedSidebarContainerWidth,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const mostRecentlyAddedCalendarItem = useSelector(
    (state) => state.calendar.data.mostRecentlyAddedCalendarItem,
  );
  const weekViewHorizontallyExpandedDay = useSelector(
    (state) => state.calendar.data.weekViewExpandedDay,
  );

  const weekDatesWhenDayToggleSelected = useMemo(() => {
    const daysToInclude = selectedDaysOfWeekToShowInCurrentWeek;
    const daysToShowMax = selectedNumberOfDaysToShowInWeek || 7;
    const includeEmptyDays = daysToInclude.includes('Empty Days');

    const dates = [];
    const datesForReduxTracking = [];

    /**
     * Sliding window: start from selectedDate (the anchor occurrence) and step
     * by 7 days, collecting up to 7 occurrences of the selected weekday.
     */
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < daysToShowMax; i++) {
      const current = moment(selectedDate).clone().add(i * 7, 'days');
      const formattedDate = current.format('YYYY-MM-DD');

      const athleteAddedItems = calendarItems[formattedDate]?.athleteAddedItems;

      const isDateEmpty = (!calendarItems[formattedDate]?.exercises
        || calendarItems[formattedDate].exercises?.length === 0)
        && (!beforeRecurringWorkouts[formattedDate]?.length
        && !afterRecurringWorkouts[formattedDate]?.length && !athleteAddedItems?.length);

      if (!isDateEmpty || includeEmptyDays) {
        dates.push(current.clone());
        datesForReduxTracking.push({ date: formattedDate, isDateEmpty });
      }
    }

    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setCurrentlyDisplayedWeekdays(datesForReduxTracking));
    }

    return dates;
  }, [
    selectedDate,
    selectedNumberOfDaysToShowInWeek,
    selectedDaysOfWeekToShowInCurrentWeek,
    calendarItems,
    selectedWeekViewDayToggle,
  ]);

  const weekDates = useMemo(() => {
    const daysToInclude = selectedDaysOfWeekToShowInCurrentWeek;
    const daysToShowMax = selectedNumberOfDaysToShowInWeek || 7;
    const includeEmptyDays = daysToInclude.includes('Empty Days');

    const dates = [];
    const datesForReduxTracking = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 7; i++) {
      const current = moment(selectedDate).clone().add(i, 'days');
      const dayName = current.format('dddd');
      const formattedDate = current.format('YYYY-MM-DD');

      const athleteAddedItems = calendarItems[formattedDate]?.athleteAddedItems;

      const isDateEmpty = (!calendarItems[formattedDate]?.exercises
        || calendarItems[formattedDate].exercises?.length === 0)
        && (!beforeRecurringWorkouts[formattedDate]?.length
        && !afterRecurringWorkouts[formattedDate]?.length && !athleteAddedItems?.length);

      /** Only add if day is toggled on AND (it's not empty OR we're allowing empty days) */
      if (
        daysToInclude.includes(dayName)
      && (!isDateEmpty || includeEmptyDays)
      ) {
        dates.push(current);
        datesForReduxTracking.push({ date: formattedDate, isDateEmpty });
      }

      if (dates?.length >= daysToShowMax) {
        break;
      }
    }

    if (selectedWeekViewDayToggle === 'Everyday') {
      dispatch(setCurrentlyDisplayedWeekdays(datesForReduxTracking));
    }
    return dates;
  }, [
    selectedDate,
    selectedNumberOfDaysToShowInWeek,
    selectedDaysOfWeekToShowInCurrentWeek,
    calendarItems,
  ]);

  const programWeekDays = useMemo(() => {
    const selectedProgramDay = programItems[selectedProgramDayNumber];

    /** If no selected day or empty programItems, return default week */
    if (!selectedProgramDay || !Object.keys(programItems)?.length) {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    const selectedWeek = selectedProgramDay.weekNumber;

    /** Get all day numbers in the selected week */
    const dayNumbersInSelectedWeek = Object.entries(programItems)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, item]) => item.weekNumber === selectedWeek)
      .map(([dayNumber]) => Number(dayNumber))
      .sort((a, b) => a - b); // ensure consistent ordering

    /** If selectedNumberOfDaysToShowInWeek is defined, apply the limit */
    if (selectedNumberOfDaysToShowInWeek && selectedNumberOfDaysToShowInWeek > 0) {
      return dayNumbersInSelectedWeek.slice(0, selectedNumberOfDaysToShowInWeek);
    }

    /** No limit applied → return all */
    return dayNumbersInSelectedWeek;
  }, [
    programItems,
    selectedProgramDayNumber,
    selectedNumberOfDaysToShowInWeek,
  ]);

  /** When the groupByFilter is changed we always reset the selected dayNumber back to the first
   * day of the program, and (on an actual toggle) re-sync the mobile preview to that same day.
   */
  useEffect(() => {
    const isGroupByToggle = didGroupByMount.current;
    didGroupByMount.current = true;
    if (programType === 'free-form-program' && viewType === 'weekView') {
      const dayNumbers = Object.keys(programItems)
        .map(Number)
        .sort((a, b) => a - b);

      if (dayNumbers?.length) {
        const lowestDayNumber = dayNumbers[0];
        if (lowestDayNumber !== selectedProgramDayNumber) {
          dispatch(setSelectedProgramDayNumber(lowestDayNumber));
        }
        // Group By is the one intentional preview re-sync point: point the mobile preview at the
        // (reset) selected day. Skip the initial run so it only fires on an actual toggle and
        // doesn't clobber a preview already open on another day when entering week view.
        if (isGroupByToggle) {
          dispatch(setPreviewDayNumber(lowestDayNumber));
        }
      }
    }
  }, [groupByFilter]);

  const programDaysGroupedByDay = useMemo(() => {
    const selectedProgramDay = programItems[selectedProgramDayNumber];

    /** If no selected day or empty programItems, return default week */
    if (!selectedProgramDay || !Object.keys(programItems)?.length) {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    const selectedDayInWeek = selectedProgramDay.dayInWeek;

    /** Get all day numbers in the selected week */
    const dayNumbersInSelectedWeek = Object.entries(programItems)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, item]) => item.dayInWeek === selectedDayInWeek)
      .map(([dayNumber]) => Number(dayNumber))
      .sort((a, b) => a - b); /** ensure consistent ordering */

    /** If selectedNumberOfDaysToShowInWeek is defined, apply the limit */
    if (selectedNumberOfDaysToShowInWeek && selectedNumberOfDaysToShowInWeek > 0) {
      return dayNumbersInSelectedWeek.slice(0, selectedNumberOfDaysToShowInWeek);
    }

    /** No limit applied → return all */
    return dayNumbersInSelectedWeek;
  }, [
    programItems,
    selectedProgramDayNumber,
    selectedNumberOfDaysToShowInWeek,
  ]);

  /** ref pattern keeps callback identity stable while reading current values */
  const toggleExpandStateRef = useRef({
    expandedDay, programType, programItems, calendarItems, expandedItems,
  });
  toggleExpandStateRef.current = {
    expandedDay, programType, programItems, calendarItems, expandedItems,
  };

  const handleToggleExpand = useCallback((dayKey) => {
    const {
      expandedDay: ed, programType: pt, programItems: pi, calendarItems: ci, expandedItems: ei,
    } = toggleExpandStateRef.current;
    const isCurrentlyExpanded = ed === dayKey;
    dispatch(setWeekViewExpandedDay(isCurrentlyExpanded ? null : dayKey));

    const allExercises = Object.values(pt === 'free-form-program' ? pi : ci).flatMap((day) => day.exercises || []);

    const newExpandedItems = {};

    allExercises.forEach((exercise) => {
      const { assignedId, type } = exercise;
      const isSessionBreak = type === 'session_break';
      if (!assignedId) return;

      /** Preserve whatever the user had — truthy keeps expanded, false keeps
       * user-collapsed (stored via SET_SESSION_BREAK_COLLAPSED). A bare truthy
       * check would drop the false entries, and the reducer's expansion helper
       * would then auto-expand them on the next setCalendarItems pass. */
      if (isSessionBreak && Object.prototype.hasOwnProperty.call(ei || {}, assignedId)) {
        newExpandedItems[assignedId] = ei[assignedId];
      }
    });

    dispatch(setExpandedItems(newExpandedItems));
  }, [dispatch]);

  const filteredWeekDates = selectedWeekViewDayToggle !== 'Everyday' ? weekDatesWhenDayToggleSelected : weekDates;
  const filteredProgramWeekDays = groupByFilter === 'Day' ? programDaysGroupedByDay : programWeekDays;

  const expandDayCallback = useCallback((dayKey) => {
    dispatch(setWeekViewExpandedDay(dayKey));
  }, [dispatch]);

  const weekDatesOrDays = programType === 'free-form-program' ? filteredProgramWeekDays : filteredWeekDates;

  /** Publish the currently-displayed program days to Redux (program library week view only).
   * Mirrors setCurrentlyDisplayedWeekdays for the date-based view. filteredProgramWeekDays already
   * reflects both the groupByFilter (Week/Day) and selectedNumberOfDaysToShowInWeek filters; we map
   * each displayed day number to its full program item so consumers get all the day's data. */
  useEffect(() => {
    if (programType === 'free-form-program') {
      const displayedProgramDays = filteredProgramWeekDays.map((dayNumber) => ({
        dayNumber,
        ...programItems[dayNumber],
      }));
      dispatch(setCurrentlyDisplayedProgramDays(displayedProgramDays));
    }
  }, [programType, filteredProgramWeekDays, programItems]);

  useEffect(() => {
    if (!weekViewHorizontallyExpandedDay || !Array.isArray(weekDatesOrDays)) return;

    /** Find the matching index */
    const matchingIndex = programType === 'free-form-program' ? weekDatesOrDays.findIndex((dayNumberIndex) => dayNumberIndex === weekViewHorizontallyExpandedDay) : weekDatesOrDays.findIndex((date) => moment(date).format('YYYY-MM-DD') === weekViewHorizontallyExpandedDay);

    if (matchingIndex === -1) {
      return;
    }

    const dayColumn = horizontalItemRefs.current[matchingIndex];
    if (dayColumn) {
      /** Skip if sidebar is open AND width is < 976 */
      if (isPrimaryAnimatedSideBarOpen && window.innerWidth < 976) {
        return; /** skip the scrollIntoView */
      }
      const delay = mostRecentlyAddedCalendarItem ? 400 : 350;
      setTimeout(() => {
        dayColumn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      }, delay);
    }
  }, [weekViewHorizontallyExpandedDay]);

  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);

  useEffect(() => {
    const checkOverflow = () => {
      const el = document.documentElement;
      const { scrollLeft, scrollWidth } = el;
      const clientWidth = window.innerWidth;

      if (leftScrollRef.current) {
        leftScrollRef.current.style.visibility = scrollLeft > 0 ? 'visible' : 'hidden';
      }

      if (rightScrollRef.current) {
        rightScrollRef.current.style.visibility = scrollLeft + clientWidth < scrollWidth ? 'visible' : 'hidden';
      }
    };

    checkOverflow(); /** run immediately */
    const timeoutId = setTimeout(checkOverflow, 350); /** after sidebar animation */

    /** re-check when the day columns themselves change size (e.g. titles widen a
     * column into overflow, or expand/collapse) — a plain scroll/resize listener
     * never sees these content-driven changes */
    const observer = new ResizeObserver(checkOverflow);
    horizontalItemRefs.current.forEach((node) => {
      if (node) observer.observe(node);
    });

    window.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      window.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [isPrimaryAnimatedSideBarOpen, weekViewHorizontallyExpandedDay, weekDatesOrDays]);

  const leftRafId = useRef(null);
  const rightRafId = useRef(null);
  const { colors } = useTheme();

  const scrollLeftStep = () => {
    const el = document.documentElement;
    if (el.scrollLeft > 0) {
      /** only scroll left if not already at leftmost */
      window.scrollBy({ left: -5, behavior: 'auto' });
      leftRafId.current = requestAnimationFrame(scrollLeftStep);
    } else {
      leftRafId.current = null; /** stop scrolling if reached left */
    }
  };

  const scrollRightStep = () => {
    const el = document.documentElement;
    if (el.scrollLeft + window.innerWidth < el.scrollWidth) {
      /** only scroll right if not already at rightmost */
      window.scrollBy({ left: 5, behavior: 'auto' });
      rightRafId.current = requestAnimationFrame(scrollRightStep);
    } else {
      rightRafId.current = null; /** stop scrolling if reached right */
    }
  };

  const handleLeftMouseEnter = () => {
    if (!leftRafId.current) leftRafId.current = requestAnimationFrame(scrollLeftStep);
  };

  const handleLeftMouseLeave = () => {
    if (leftRafId.current) {
      cancelAnimationFrame(leftRafId.current);
      leftRafId.current = null;
    }
  };

  const handleRightMouseEnter = () => {
    if (!rightRafId.current) rightRafId.current = requestAnimationFrame(scrollRightStep);
  };

  const handleRightMouseLeave = () => {
    if (rightRafId.current) {
      cancelAnimationFrame(rightRafId.current);
      rightRafId.current = null;
    }
  };

  return (
    <>
      <ScrollIconBox
        ref={leftScrollRef}
        leftAnchor
        sidebarOpen={isPrimaryAnimatedSideBarOpen}
        tvMode={isTvMode}
        style={{
          position: 'fixed',
          top: '50%',
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleLeftMouseEnter}
        onMouseUp={handleLeftMouseLeave}
        onMouseLeave={handleLeftMouseLeave}
        onTouchStart={handleLeftMouseEnter}
        onTouchEnd={handleLeftMouseLeave}
        onTouchCancel={handleLeftMouseLeave}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='left-arrow'
          color={colors.text}
        />
      </ScrollIconBox>

      <WeekContainer
        ref={weekViewContainerRef}
        viewType={viewType}
        isTvMode={isTvMode}
      >
        {weekDatesOrDays?.map((dateOrDay, index) => {
          const dayKey = programType === 'free-form-program' ? dateOrDay : dateOrDay.format('YYYY-MM-DD');
          const isExpanded = expandedDay === dayKey;
          /** Program day totals are derived from the day's exercises (see utils/programDayTotals);
           * calendar days read the stored, backend-computed values. Derived once per day rather
           * than per stat. */
          const dayTotals = programType === 'free-form-program'
            ? getProgramDayTotals(programItems[dateOrDay])
            : {
              setCount: calendarItems[dayKey]?.setCount || 0,
              repCount: calendarItems[dayKey]?.repCount || 0,
              totalVolumeLoad: calendarItems[dayKey]?.totalVolumeLoad || 0,
            };

          return (
            <DayWrapper
              weekDatesOrDaysLength={weekDatesOrDays?.length}
              key={`day-${dayKey}`}
              isExpanded={isExpanded}
              ref={(el) => (horizontalItemRefs.current[index] = el)}
            >
              <UnifiedDayHeader
                programType={programType}
                viewType='weekView'
                date={dateOrDay}
                isExpanded={isExpanded}
                onToggleExpand={handleToggleExpand}
                dayKey={dayKey}
                showWorkoutName
                isWeekView
                dateOrDay={dateOrDay}
                filteredWeekDates={filteredWeekDates}
                filteredProgramWeekDays={filteredProgramWeekDays}
              />
              <DayColumn
                isWeekDayItemsLoading={isWeekDayItemsLoading}
                addPaddingLeftAndRight={((String(weekViewHorizontallyExpandedDay)
                === String(dayKey)))}
              >
                {isWeekDayItemsLoading ? (
                  shimmerCount.map((_, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex', paddingLeft: 8, paddingRight: 8, paddingTop: i === 0 ? 8 : 4, paddingBottom: 4,
                      }}
                    >
                      <Shimmer>
                        <div
                          style={{
                            height: 64,
                            backgroundColor: colors.surfaceSecondary,
                            width: '100%',
                          }}
                        />
                      </Shimmer>
                    </div>
                  ))
                ) : (
                  <>
                    <DraggableContainer
                      id={`week-day-${dayKey}`}
                      date={dateOrDay}
                      dayKey={dayKey}
                      isWeekView
                      isDayExpanded={isExpanded}
                      onDayExpand={handleToggleExpand}
                      expandDay={expandDayCallback}
                    />
                    {((Object.keys(selectedReduxCalendar)?.length
                || Object.keys(selectedReduxAthlete)?.length
                || Object.keys(selectedReduxNonDateBasedProgram)?.length))
                && !isAnalyticsDisabled ? (
                  <WeekDayStatsBar
                    isExpanded={isExpanded}
                    sets={dayTotals.setCount}
                    reps={dayTotals.repCount}
                    volume={dayTotals.totalVolumeLoad}
                  />
                      ) : null}
                  </>
                )}
              </DayColumn>
            </DayWrapper>
          );
        })}
        {/* we need to add fixed position margins because
        nested scroll only work if we allow the browser to control scroll
        this mean we lose our margins that we would get from
        a nested scroll container so we are just creating them for style  */}
        {!isTvMode && (
          <MarginLeft
            headerHeight={headerHeight}
            animatedSidebarContainerWidth={animatedSidebarContainerWidth}
            isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
            programType={programType}
          />
        )}
        {!isTvMode && (
          <MarginRight
            headerHeight={headerHeight}
            programType={programType}
            isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
          />
        )}
      </WeekContainer>
      <ScrollIconBox
        ref={rightScrollRef}
        sidebarOpen={isPrimaryAnimatedSideBarOpen}
        style={{
          position: 'fixed',
          top: '50%',
          right: 12,
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleRightMouseEnter}
        onMouseUp={handleRightMouseLeave}
        onMouseLeave={handleRightMouseLeave}
        onTouchStart={handleRightMouseEnter}
        onTouchEnd={handleRightMouseLeave}
        onTouchCancel={handleRightMouseLeave}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='right-arrow'
          color={colors.text}
        />
      </ScrollIconBox>
    </>
  );
};

WeekView.propTypes = {
  isWeekDayItemsLoading: PropTypes.bool,
};

WeekView.defaultProps = {
  isWeekDayItemsLoading: false,

};

export default WeekView;
